import React from 'react';
import {useHistory} from 'react-router-dom';

import {buildUrlWithQueryParameters} from '@famly/vet_ts-utils_url';
import {type QueryParams} from '@famly/vet_navigation_route-config';

type Options = {
    queryParams?: QueryParams['queryParams'];
};

/**
 * @returns A function that can replace the current path in the history stack.
 *
 * The function takes a path as an argument and replaces the current path in the history stack.
 *
 * This function is useful if you find yourself with a path that e.g. comes from
 * an untyped source (e.g. local storage, a backend resource etc.).
 *
 * The `path` arugment is expected to be a fully "built" path. I.e. it cannot contain any parameters.
 * Example of path with parameters: `/account/childProfile/:childId/activity`.
 * Example of path without parameters: `/account/childProfile/15d9bcc5-1bf0-4081-90f3-1a0ab9a62f5d/activity`.
 *
 * @example
 * ```tsx
 * const replaceWithUnsafePath = useReplaceWithUnsafePath();
 *
 * replaceWithUnsafePath(unsafePath, {queryParams: {id: 123}});
 * ```
 */
export const useReplaceWithUnsafePath = () => {
    const {replace} = useHistory();

    const replaceWithUnsafePath = React.useCallback(
        (path: string, options?: Options) => {
            const pathWithQueryParams = buildUrlWithQueryParameters(path, options?.queryParams ?? {});

            replace(pathWithQueryParams);
        },
        [replace],
    );

    return replaceWithUnsafePath;
};
